import EventSource from "eventsource";
import { ConnectionError } from "./errors";
import type { Stream, StreamEvent, StreamEventHandler } from "./types";

const AUTHORIZATION_HEADER = "Authorization";

// TODO: Create an `AsyncIterator` that can be used in a `for await` loop, code in comments
export function iterateEventSource<T>(request: Request): Stream<T> {
	const url = request.url;
	const bearer = request.headers.get(AUTHORIZATION_HEADER);

	const evtSrc = new EventSource(url, {
		headers: {
			[AUTHORIZATION_HEADER]: bearer,
		},
	});
	const handlers = new Map<StreamEvent, StreamEventHandler[]>();

	const dispose = (): void => {
		evtSrc.close();
		handlers.clear();
	};

	const broadcast = <TEventData>(
		eventName: StreamEvent,
		data: TEventData,
	): void => {
		if (!handlers.has(eventName)) {
			return;
		}

		handlers.get(eventName)?.forEach((listener) => {
			void listener(data);
		});
	};

	const subscribe = (
		eventName: StreamEvent,
		listener: StreamEventHandler<T>,
	): void => {
		if (!handlers.has(eventName)) {
			handlers.set(eventName, []);
		}

		handlers.get(eventName)?.push(listener);
	};

	const unsubscribe = (
		eventName: StreamEvent,
		listener: StreamEventHandler<T>,
	): void => {
		if (!handlers.has(eventName)) {
			return;
		}

		const listeners = handlers.get(eventName);
		const idx = listeners?.indexOf(listener);
		if (idx !== undefined && idx !== -1) {
			listeners?.splice(idx, 1);
		}
	};

	evtSrc.addEventListener("open", (evt) => {
		broadcast("connected", new Date(evt.data as string).getTime());
	});

	evtSrc.addEventListener("ping", (evt) => {
		broadcast("ping", new Date(evt.data as string).getTime());
	});

	evtSrc.addEventListener("message", (evt) => {
		const value = JSON.parse(evt.data as string) as T;

		// NOTE: This is bespoke to our implementation from the server
		if (typeof value === "string" && value.includes("ping")) {
			console.log(value);
		}

		broadcast("message", value);
	});

	evtSrc.addEventListener("close", () => {
		evtSrc.close();
		broadcast("disconnected", null);
	});

	evtSrc.addEventListener("error", (evt) => {
		broadcast("error", new ConnectionError(JSON.stringify(evt)));
	});

	return {
		dispose,
		on: subscribe,
		off: unsubscribe,
		readyState: () => evtSrc.readyState,
	};
}
